import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import PageLayout from '../components/PageLayout';
import PageHero from '../components/PageHero';
import FeatureList from '../components/FeatureList';
import { customSoftwareContent } from '../data/customSoftware';

const MOCKUP_IMAGE = '/ticket-system-mockup.png';
const MOCKUP_ALT = 'Custom worker ticketing system mockup';

export default function CustomSoftware() {
  const [isExpanded, setIsExpanded] = useState(false);
  const { outcome, description, bestFor, highlights, features, price } = customSoftwareContent;

  const closeExpanded = useCallback(() => setIsExpanded(false), []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    if (!isExpanded) return undefined;

    const onKeyDown = (e) => {
      if (e.key === 'Escape') closeExpanded();
    };

    window.addEventListener('keydown', onKeyDown);
    document.body.style.overflow = 'hidden';

    return () => {
      window.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = '';
    };
  }, [isExpanded, closeExpanded]);

  return (
    <PageLayout>
      <PageHero
        eyebrow="Custom builds"
        title="Customized Software"
        subtitle="Worker ticketing, audit trails, and workflows built around how your team actually operates."
        className="pb-8 md:pb-10"
        maxWidth="max-w-4xl"
      />

      <section className="px-4 md:px-8 pb-20">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-8 md:mb-10 max-w-3xl mx-auto">
            <h2
              className="text-2xl md:text-3xl font-bold mb-3 text-gray-700"
              style={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, letterSpacing: '0.05em' }}
            >
              Ticket System Mockup
            </h2>
            <p
              className="text-base md:text-lg mb-3"
              style={{ fontFamily: "'Poppins', sans-serif", color: '#292929' }}
            >
              A preview of how we structure worker ticketing: assignments, requests, follow ups, and visibility for your team.
            </p>
            <p
              className="text-sm text-gray-600"
              style={{ fontFamily: "'Poppins', sans-serif" }}
            >
              Tap the image to expand it
            </p>
          </div>

          <article
            className="rounded-2xl overflow-hidden border border-white/30 bg-white/15 backdrop-blur-sm shadow-xl max-w-4xl mx-auto cursor-pointer group transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl mb-10 md:mb-14"
            onClick={() => setIsExpanded(true)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setIsExpanded(true);
              }
            }}
            role="button"
            tabIndex={0}
            aria-label="Ticket system mockup. Click to expand"
          >
            <div className="relative">
              <img
                src={MOCKUP_IMAGE}
                alt={MOCKUP_ALT}
                className="w-full h-auto block transition-transform duration-300 group-hover:scale-[1.02]"
              />
              <div
                className="absolute inset-0 flex items-end justify-center pb-4 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"
              >
                <span
                  className="px-4 py-2 rounded-full text-white text-sm font-medium shadow-lg"
                  style={{ backgroundColor: '#ff7a59', fontFamily: "'Poppins', sans-serif" }}
                >
                  Click to expand
                </span>
              </div>
            </div>
          </article>

          <div className="max-w-3xl mx-auto mb-10 md:mb-14 text-center">
            <p
              className="text-base md:text-lg font-medium mb-4"
              style={{ fontFamily: "'Poppins', sans-serif", color: '#292929' }}
            >
              {outcome}
            </p>
            <p
              className="text-base md:text-lg leading-relaxed mb-4"
              style={{ fontFamily: "'Poppins', sans-serif", color: '#292929' }}
            >
              {description}
            </p>
            <p
              className="inline-block text-xs md:text-sm px-4 py-2 rounded-full bg-white/40 border border-white/50 mb-5"
              style={{ fontFamily: "'Poppins', sans-serif", color: '#292929' }}
            >
              {bestFor}
            </p>
            {price && (
              <p
                className="inline-block text-sm font-semibold px-4 py-2 rounded-full text-white shadow-md"
                style={{ backgroundColor: '#ff7a59', fontFamily: "'Poppins', sans-serif" }}
              >
                {price}
              </p>
            )}
            {highlights.length > 0 && (
              <ul className="flex flex-wrap justify-center gap-2 mt-6">
                {highlights.map((highlight) => (
                  <li
                    key={highlight}
                    className="text-xs px-3 py-1.5 rounded-full bg-[#ff7a59]/15 border border-[#ff7a59]/25"
                    style={{ fontFamily: "'Poppins', sans-serif", color: '#292929' }}
                  >
                    {highlight}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="bg-white/12 backdrop-blur-sm rounded-2xl border border-white/25 shadow-md p-6 md:p-8 mb-10 md:mb-14 max-w-3xl mx-auto">
            <FeatureList features={features} />
          </div>

          <div className="text-center max-w-xl mx-auto">
            <p
              className="text-base md:text-lg mb-6"
              style={{ fontFamily: "'Poppins', sans-serif", color: '#292929' }}
            >
              Want something like this for your business? Book a free consult and we will scope your build.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/book" className="group">
                <button
                  className="px-8 py-3 text-white font-medium rounded-full shadow-lg transition-all duration-300 transform hover:scale-105"
                  style={{ backgroundColor: '#ff7a59', fontFamily: "'Poppins', sans-serif" }}
                >
                  Book a consultation
                </button>
              </Link>
              <Link to="/services#custom-software" className="group">
                <button
                  className="px-8 py-3 font-medium rounded-full border-2 border-[#ff7a59] bg-white/40 backdrop-blur-sm text-gray-700 transition-all duration-300 transform hover:scale-105"
                  style={{ fontFamily: "'Poppins', sans-serif" }}
                >
                  All services
                </button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {isExpanded && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-8 bg-black/75 backdrop-blur-sm"
          onClick={closeExpanded}
          role="dialog"
          aria-modal="true"
          aria-label="Ticket system mockup expanded"
        >
          <button
            type="button"
            onClick={closeExpanded}
            className="absolute top-6 right-6 z-10 w-10 h-10 rounded-full bg-white/90 text-gray-700 text-xl leading-none shadow-lg hover:bg-white transition-colors"
            aria-label="Close expanded view"
          >
            ×
          </button>
          <img
            src={MOCKUP_IMAGE}
            alt={MOCKUP_ALT}
            className="max-h-[90vh] max-w-full w-auto rounded-2xl shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </PageLayout>
  );
}
